import { Injectable, inject } from '@angular/core';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppHttpClientService } from '../shared/app-http-client.service';
import { Auction } from './model';
import { AppDatabaseService } from '../shared/database/app-database.service';
import { MonitoringService } from '../shared/monitoring/monitoring.service';

@Injectable({
  providedIn: 'root',
})
export class AuctionService {
  private readonly database = inject(AppDatabaseService);

  private readonly changed$ = new BehaviorSubject<boolean>(false);
  private readonly auctions$ = new BehaviorSubject<Auction[]>([]);
  private auctionsList: Auction[] = [];
  private loaded = false;

  private readonly httpClient = inject(AppHttpClientService);
  private readonly monitoring = inject(MonitoringService);

  public load() {
    return new Promise<void>((resolve, reject) => {
      if (this.loaded) {
        console.log('auctions already loaded');
        resolve();
        return;
      }

      this.httpClient
        .get<Auction[]>('auctions')
        .then((auctions) => {
          if (auctions) {
            this.auctionsList = auctions;
            this.auctions$.next(this.auctionsList);
            console.log('auctions loaded:', this.auctionsList);

            this.auctionsList.forEach((auction) => {
              this.observeAuction(auction.id).subscribe((data) => {
                console.log('auction data changed:', auction.id, data);
                const index = this.auctionsList.findIndex((a) => a.id === auction.id);
                if (index < 0) {
                  return;
                }
                const current = this.auctionsList[index];
                this.auctionsList[index] = {
                  ...current,
                  isRunning: !!data.isRunning,
                  startedAt: data.startedAt as Auction['startedAt'],
                };
                this.auctions$.next([...this.auctionsList]);
                this.changed$.next(true);
              });
            });

            this.loaded = true;
          }
          resolve();
        })
        .catch((error: unknown) => {
          this.monitoring.trackEvent('auctions_load_failed');
          this.monitoring.captureException(error);
          reject(error);
        });
    });
  }

  public isAnyAuctionRunning() {
    return this.auctionsList.some((auction) => auction.isRunning);
  }

  public changed() {
    return this.changed$.asObservable().pipe(filter((f) => !!f));
  }

  public getAuctions() {
    return this.auctions$.asObservable();
  }

  private startedAtComparable(value: unknown): number | null {
    if (value === undefined || value === null) {
      return null;
    }
    if (value instanceof Date) {
      return value.getTime();
    }
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object') {
      const record = value as { _seconds?: unknown; seconds?: unknown };
      if (typeof record.seconds === 'number') {
        return record.seconds;
      }
      if (typeof record._seconds === 'number') {
        return record._seconds;
      }
    }
    return null;
  }

  public observeAuction(id: string) {
    return new Observable<any>((observer) => {
      let unsubscribe: (() => void) | undefined;
      try {
        unsubscribe = this.database.readLive(`${id}/exec`, (doc) => {
          const data = doc.data();
          observer.next({
            isRunning: data?.['isRunning'],
            state: data?.['state'],
            startedAt: data?.['runningOpenDate'],
          } as Auction);
        });
      } catch (error: unknown) {
        this.monitoring.trackEvent('auction_live_data_failed', { auctionId: id });
        this.monitoring.captureException(error);
        observer.error(error);
      }

      return () => unsubscribe?.();
    }).pipe(
      distinctUntilChanged(
        (a: { isRunning?: boolean; startedAt?: unknown }, b: { isRunning?: boolean; startedAt?: unknown }) =>
          a?.isRunning === b?.isRunning &&
          this.startedAtComparable(a?.startedAt) === this.startedAtComparable(b?.startedAt)
      )
    );
  }
}
